import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
// `arrayUnion` comes from the facade and `serverTimestamp` from the SDK (#126).
// The offline queue materialises a write so it can survive a reload, and it
// reconstructs a `serverTimestamp()` from the SDK's own `FieldValue` class, so
// that one is interchangeable. `arrayUnion`'s *elements* are private to the
// SDK's instance, so a queued write built from the SDK's version could not be
// rebuilt; `src/data/opCodec.js` refuses it by name.
import { arrayUnion, writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import { occurrencesBetween as foldOccurrences, schedulesOf } from '../domain/schedule';
import { coreSync } from '../lib/core';
import { ANALYTICS, trackEvent } from '../lib/analytics';
import {
  newSeriesDocument, seriesUpdateDocument, occurrenceOverrideDocument, occurrenceRemovalDocument,
  overridesToDrop,
} from '../domain/eventSeries';
import { categoryRelabelDocument } from '../domain/categories';
import { normaliseTimeZone } from '../domain/timeZone';
import { browserTimeZone } from '../lib/intlOptions';
import { useSettings } from './useSettings';
import { useCollectionWindow } from './useCollectionWindow';

// Calendar events.
//
// Every event is a *series* document, including a one-time event — a series
// that ends the day it starts — so expansion, pricing and attendance have one
// code path, and "make this repeat" is an edit rather than a conversion.
//
// A series that repeats indefinitely has no finite occurrence list, so nothing
// is materialized up front. Occurrences are expanded on demand for whatever
// window the calendar is showing, and a document is written for one only when
// it deviates from its series — moved, re-priced, re-timed — or has been
// touched by attendance. Those override documents use the deterministic
// occurrence id, so two devices editing the same Tuesday converge.
//
// An occurrence that has been *removed* is the one deviation that is not an
// override document: the rule date goes into the series' `exceptions` and the
// override, if there was one, is deleted. See `removeOccurrence` below and the
// note at the top of domain/schedule.js: there is no cancelled state, as in
// MyMusicStaff.

const EventsContext = createContext(null);
const TRACK_LIMIT = 2000;

// Re-exported from the domain, where it belongs: the drawer and the expansion
// both need it, and neither should have to import from the Firebase layer to
// get at a set of string constants.
export { RECURRENCE_MODES } from '../domain/recurrence';

export function EventsProvider({ children }) {
  const { user } = useAuth();
  // How long a temporary weekly hold runs. A studio setting rather than a
  // constant — MMS's "Weekly time slot hold duration" — so a studio set to 14
  // days gets a fourteen-day hold. SettingsProvider sits outside this one in
  // App.jsx and in the test harness.
  const { settings } = useSettings();
  const weeklyHoldDays = settings.scheduling.weeklyHoldDays;
  // The zone a *new* series is stamped with, and only that. It is not the
  // interpreter of the ones already written: see domain/timeZone.js. The
  // browser's zone stands in when the studio has never opened the profile
  // drawer, because a series with no zone at all is a lesson nobody can label,
  // and the machine that scheduled it says where the teacher was — as
  // lib/intlOptions.js `displayTimeZone` reasons.
  const studioTimeZone = normaliseTimeZone(settings.timeZone) || browserTimeZone() || null;
  // Two windows, two collections, two calls (#207 U5): `eventSeries` and
  // `occurrences`, both at TRACK_LIMIT and both with metadata.
  const seriesWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.eventSeries,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const overridesWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.occurrences,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const series = seriesWindow.docs;
  const overrides = overridesWindow.docs;
  // `loading` is cleared by the series listener alone.
  const loading = seriesWindow.loading;
  // The moves and cancellations over the series, read or not. A lesson is
  // expanded from both, so a report that folds lessons waits for both (#646).
  const overridesLoading = overridesWindow.loading;

  // ── Memoised on the *result*, keyed by window (#214) ────────────────────
  //
  // Not an index of the overrides: the fold is the core's, and an index that
  // drops nothing cannot reduce what crosses — a memoised index over a core
  // call is the crossing plus a redundant walk. See the note at
  // `occurrencesBetween` in `domain/schedule.js`.
  //
  // What a calendar actually re-asks for is the **same window, several times a
  // render**: the grid, the agenda beside it and the day count all ask for the
  // week being drawn. So the cache is keyed by the window and cleared whenever
  // the documents change.
  //
  // A plain object rather than a `Map`: the keys are two date strings joined,
  // there are a handful per screen, and it is rebuilt on every snapshot rather
  // than evicted from.
  const windows = useMemo(() => ({}), [series, overrides]);

  // Every series as a *schedule* — the rule plus the per-entry deviations —
  // which is the shape domain/conflicts.js compares.
  const conflictSchedules = useMemo(() => schedulesOf(series, overrides), [series, overrides]);

  const value = useMemo(() => {
    // Expand every series across a window and return flat, dated occurrences.
    //
    // The rule is `core/src/schedule.rs`, reached through `domain/schedule.js`,
    // which shapes the documents into the boundary's five joined lists and
    // rejoins the two flat answers that come back. This provider supplies the
    // documents and caches the result; it does not decide anything.
    const occurrencesBetween = (startISO, endISO) => {
      const key = `${startISO} ${endISO}`;
      return (windows[key] ||= foldOccurrences(series, overrides, startISO, endISO));
    };

    // Which overrides belong to a series is `overridesToDrop` in
    // domain/eventSeries.js: each is paired with the rule date it is keyed by,
    // and one that names neither is left alone — overrideOccurrence below stamps
    // both onto everything it writes. `overrideKey` is the core's: whether a
    // stored document is identified well enough to join a series is a rule
    // about the data, not about how Firestore holds it (#214).
    //
    // Deleted rather than tombstoned: unlike a ledger entry these carry no
    // money and no history worth converging on, and a tombstone would be just
    // as unreachable as the thing it replaced.
    const deleteOverrides = async (ids) => {
      if (ids.length === 0) return 0;
      const batch = writeBatch(db);
      for (const id of ids) batch.delete(docRef(user.uid, COLLECTIONS.occurrences, id));
      await batch.commit();
      return ids.length;
    };

    return {
      loading,
      // The read stopped before it answered (#647) — src/domain/reading.js.
      failed: seriesWindow.failed,
      overridesLoading,
      series,
      // The occurrence overrides, raw, for the category merge (#142), which
      // re-points every document carrying a `categoryId` — `eventSeries`,
      // `students` and these.
      //
      // Everything else asks `occurrencesBetween` or `overridesToDrop`: an
      // override on its own is a deviation from a rule it does not carry.
      overrides,
      conflictSchedules,
      // The studio's hold duration, passed on rather than re-read: the drawer
      // derives an end date from the same number this provider writes with.
      weeklyHoldDays,
      occurrencesBetween,
      occurrencesOn: (dateISO) => occurrencesBetween(dateISO, dateISO),
      // Count used by the onboarding milestone. Bounded window rather than a
      // full scan: "has this studio ever scheduled anything" only needs to
      // find one, and series existing at all is a sufficient answer.
      eventCount: series.length,

      createSeries: async (input) => {
        if (!user) return null;
        // The first calendar entry of any kind, as `eventCount` and the
        // getting-started checklist count it (#458, src/lib/analyticsEvents.js).
        const first = !loading && series.length === 0;
        const id = freeId('ser');
        const batch = writeBatch(db);
        // The document is `newSeriesDocument` in domain/eventSeries.js. Two of
        // its fields are decided here, because they come from this provider:
        //
        // * `timeZone`, the zone `startTime` is a wall clock in, serialized
        //   here and never rewritten afterwards — see domain/timeZone.js for
        //   the argument, and note that `updateSeries` merges, so nothing can
        //   rewrite what it does not send. From the studio's *current* setting
        //   only when the caller has no zone of its own: splitting a series
        //   with "this and following" hands the tail the original rule's zone,
        //   because it is the same lessons for the same student, and a teacher
        //   who has since moved must not shift them by editing something else.
        // * `endDate`, derived from the studio's own hold duration, so this
        //   path and the drawer's cannot disagree about how long "temporary
        //   weekly hold" is.
        batch.set(docRef(user.uid, COLLECTIONS.eventSeries, id), newSeriesDocument(input, {
          weeklyHoldDays,
          timeZone: normaliseTimeZone(input.timeZone) || studioTimeZone,
          serverTimestamp: serverTimestamp(),
        }));
        await batch.commit();
        if (first) trackEvent(ANALYTICS.firstEventScheduled);
        return id;
      },

      // A merge, which is what keeps `timeZone` immutable without a guard here:
      // seriesFieldsFromForm does not emit one, so no edit to a rule can carry
      // one, and a settings change writes to settings/studio. Moving a series
      // to a new zone is the explicit bulk edit #47 describes, with a
      // confirmation, since it moves every family's lesson time.
      updateSeries: async (id, patch) => {
        if (!user) return;
        const batch = writeBatch(db);
        batch.set(
          docRef(user.uid, COLLECTIONS.eventSeries, id),
          seriesUpdateDocument(patch, serverTimestamp()),
          { merge: true },
        );
        await batch.commit();
      },

      // ── Renaming a category carries its events with it ────────────────────
      //
      // A category's name is denormalised onto every event that uses it —
      // `categoryLabel` on the series, and on an occurrence override where "edit
      // this entry only" changed it (core/src/schedule.rs prefers the
      // override's label to the series'). That is what lets the calendar,
      // the reports and the reminder emails name a lesson without joining a
      // second collection, so the rename in #98's Edit Category drawer also
      // relabels the events, as `renameCategory` in useExpenses.jsx relabels
      // entries. Resolving at read time from `categoryId` instead would reach
      // the fold in `core/src/schedule.rs` and the server's raw-document read in
      // `functions/delivery/facts.js`, which has no category collection.
      //
      // **Matched by id, never by name.** An event stores both, so an event
      // carrying a `categoryLabel` with no `categoryId` is left alone rather
      // than rewritten because its text happens to match.
      //
      // **Idempotent, and only what changed.** Documents already carrying the
      // new label are not written, so a rename interrupted half way is repaired
      // by saving the category again.
      //
      // This reads the series and overrides this provider holds (capped at
      // TRACK_LIMIT, as the calendar is), and chunks at 400 because a Firestore
      // batch holds 500 writes. The chunks are separate commits.
      relabelCategory: async ({ categoryId, label }) => {
        if (!user || !categoryId || !label) return 0;
        const targets = [
          ...series
            .filter((s) => s.categoryId === categoryId && s.categoryLabel !== label)
            .map((s) => docRef(user.uid, COLLECTIONS.eventSeries, s.id)),
          ...overrides
            .filter((o) => o.categoryId === categoryId && o.categoryLabel !== label)
            .map((o) => docRef(user.uid, COLLECTIONS.occurrences, o.id)),
        ];
        for (let i = 0; i < targets.length; i += 400) {
          const batch = writeBatch(db);
          for (const ref of targets.slice(i, i + 400)) {
            batch.set(ref, categoryRelabelDocument(label, serverTimestamp()), { merge: true });
          }
          await batch.commit();
        }
        return targets.length;
      },

      // Editing a single occurrence writes an override rather than touching the
      // series, so the rest of the series is unaffected and the change survives
      // a later edit to the series defaults.
      overrideOccurrence: async (occId, patch) => {
        if (!user) return;
        const batch = writeBatch(db);
        // Stamp the two facts the id is built from onto the document as well,
        // so the document says which series and date it belongs to without
        // parsing the id.
        batch.set(
          docRef(user.uid, COLLECTIONS.occurrences, occId),
          occurrenceOverrideDocument(occId, patch, serverTimestamp()),
          { merge: true },
        );
        await batch.commit();
      },

      // Delete → "This event" on one entry of a recurring series.
      //
      // **The entry is removed, not marked**, as MyMusicStaff's delete leaves
      // the day empty. A standalone lesson can be deleted outright (see
      // deleteSeries, and the one-time branch in CalendarPage); one Tuesday of
      // a rule that produces every Tuesday cannot, so the rule records the hole
      // in `exceptions`, which the expansion skips.
      //
      // There is no `cancelled: true` on the override document (#152, #154):
      // that would take the lesson out of the schedule while every view went on
      // drawing it struck through, and nothing could undo it. An entry is
      // produced or it is not, and calling a lesson off without removing it is
      // the `Teacher Cancelled` attendance status, reversible from the same
      // dropdown that set it.
      //
      // Two writes in one batch, and both are needed:
      //
      //   * `arrayUnion` on the series rather than a read-modify-write, so two
      //     devices deleting two different Tuesdays offline both survive the
      //     merge instead of the later one erasing the earlier. It is also
      //     idempotent, which matters because this is an offline write that may
      //     be replayed.
      //   * the override document is **deleted**. It is keyed by a rule date the
      //     rule no longer reaches, so nothing will read it again — the same
      //     unreachability `dropOverridesFrom` culls for.
      //
      // Attendance and ledger documents for the occurrence are not touched
      // here: voiding a charge is a money decision made in the delete dialog
      // (#157), which states what money the lesson carries and offers to
      // retract it, and CalendarPage calls `voidAttendanceEffects` when the
      // teacher says yes. See the header of domain/lessonRemoval.js. This
      // function is the schedule write only.
      removeOccurrence: async (occId) => {
        if (!user) return;
        const from = coreSync().parseOccurrenceId(occId);
        if (!from) return;
        const batch = writeBatch(db);
        batch.set(
          docRef(user.uid, COLLECTIONS.eventSeries, from.seriesId),
          occurrenceRemovalDocument(occId, { serverTimestamp: serverTimestamp(), arrayUnion }),
          { merge: true },
        );
        batch.delete(docRef(user.uid, COLLECTIONS.occurrences, occId));
        await batch.commit();
      },

      // Overrides belonging to a series from a date onward.
      //
      // Splitting a series ("this and following") stops the old rule and starts
      // a new one. Any override on or after the split date was keyed from the
      // old series id, but those dates are produced by the new series, so
      // nothing will read them again. Left behind they would make the
      // occurrences collection disagree with the calendar.
      dropOverridesFrom: async (seriesId, fromDate) => {
        if (!user) return 0;
        return deleteOverrides(overridesToDrop(overrides, seriesId, fromDate));
      },

      // Every override a series has, whatever date it sits on.
      //
      // For the other way a schedule is rewritten: editing a rule *in place* so
      // that it produces different dates. `dropOverridesFrom(id, oldStartDate)`
      // is not the same thing and not enough — a series that was shifted later
      // at some point has overrides on dates before its own start, and those
      // are precisely the ones already orphaned, so a "from" cut leaves the
      // worst of them behind. See useEventForm for what MMS does here.
      dropAllOverrides: async (seriesId) => {
        if (!user) return 0;
        return deleteOverrides(overridesToDrop(overrides, seriesId));
      },

      deleteSeries: async (id) => {
        if (!user) return;
        const batch = writeBatch(db);
        batch.delete(docRef(user.uid, COLLECTIONS.eventSeries, id));
        await batch.commit();
      },

      // What one occurrence costs this student, or nothing where it cannot be
      // priced from the occurrence alone.
      //
      // **Nothing means two different things, and a caller must not try to
      // tell them apart from the answer** (`price_occurrence` in
      // core/src/pricing.rs): "the month's fee covers it" for a
      // fixed-monthly student — a charge invoice generation writes for the
      // month (functions/invoicing/monthlyFees.js, #787) — and "this event is
      // all-day, so there is no duration to price from" for an hourly one
      // (#322). Either way, do not invent a charge.
      //
      // `EventPopover` prints nothing for either. The trend's forecast does not
      // call this: it prices through the core's copy of this rule,
      // `project_teaching` in core/src/teaching.rs (#470), which reads the
      // *mode* rather than a `null` — an input it already holds.
      priceFor: (occurrence, student) => {
        if (occurrence.pricingRule === 'none') return 0;
        if (occurrence.pricingRule === 'fixed' && occurrence.priceOverrideMinor != null) {
          return occurrence.priceOverrideMinor;
        }
        return coreSync().priceOccurrence(
          student?.pricingMode, student?.rateMinor, occurrence.durationMin,
          occurrence.allDay,
        );
      },
    };
    // `studioTimeZone` belongs here, as `weeklyHoldDays` does: createSeries
    // closes over it, so without it a studio that set its zone and then
    // scheduled a lesson in the same session would stamp the series with the
    // stale zone (test/integration/lessonTimeZone).
  }, [series, overrides, windows, conflictSchedules, weeklyHoldDays, studioTimeZone, loading, seriesWindow.failed, overridesLoading, user]);

  return <EventsContext.Provider value={value}>{children}</EventsContext.Provider>;
}

export function useEvents() {
  const ctx = useContext(EventsContext);
  if (!ctx) throw new Error('useEvents must be used within an EventsProvider');
  return ctx;
}
